import { z } from 'zod'

export const FRIENDLY_EMAIL_MSG = 'Enter a valid email (example: you@example.com).'
export const EMPTY_EMAIL_MSG = 'Enter your email before checkout.'
export const CRYPTO_CHECKOUT_UNAVAILABLE_MSG =
  'Crypto checkout is unavailable right now. Try card or PayPal, or contact support.'

/** SHKeeper / wallet daemons sometimes return bare "address" when deposit generation fails. */
export function normalizeCryptoProcessorMessage(raw: string | undefined): string {
  const msg = (raw ?? '').trim()
  if (!msg) return CRYPTO_CHECKOUT_UNAVAILABLE_MSG
  const bare = msg.replace(/^['"]|['"]$/g, '').trim().toLowerCase()
  if (bare === 'address' || bare === 'wallet' || bare === 'addr') {
    return CRYPTO_CHECKOUT_UNAVAILABLE_MSG
  }
  if (/wallet address|deposit address|did not return a wallet/i.test(msg)) {
    return CRYPTO_CHECKOUT_UNAVAILABLE_MSG
  }
  if (/payment gateway is unavailable|not configured|no api key/i.test(msg)) {
    return CRYPTO_CHECKOUT_UNAVAILABLE_MSG
  }
  return msg
}

/** Strip invisible / mobile-keyboard quirks before validation. */
const INVISIBLE_RE = /[\u200B-\u200D\uFEFF\u00AD]/g

type ZodLikeIssue = {
  message?: string
  path?: Array<string | number>
  code?: string
  format?: string
}

export function sanitizeBuyerEmailInput(raw: string): string {
  return raw
    .normalize('NFKC')
    .replace(/\uFF20/g, '@')
    .replace(INVISIBLE_RE, '')
    .trim()
    .toLowerCase()
}

/** Prefer form field / DOM value (autofill often skips React state). */
export function resolveBuyerEmail(stateValue: string, inputEl?: HTMLInputElement | null): string {
  if (inputEl?.form) {
    const fromForm = inputEl.form.elements.namedItem('email')
    if (fromForm instanceof HTMLInputElement && fromForm.value.trim()) {
      return sanitizeBuyerEmailInput(fromForm.value)
    }
  }
  const fromDom = inputEl?.value ?? ''
  return sanitizeBuyerEmailInput(fromDom || stateValue)
}

export function normalizeBuyerEmail(raw: string): string {
  return sanitizeBuyerEmailInput(raw)
}

/** Very permissive — only rejects obvious non-emails. */
export function isValidBuyerEmail(raw: string): boolean {
  const e = sanitizeBuyerEmailInput(raw)
  if (!e || e.length < 5 || e.length > 254) return false
  const at = e.indexOf('@')
  if (at < 1 || at >= e.length - 2) return false
  const domain = e.slice(at + 1)
  if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) return false
  return !/\s/.test(e)
}

/** User-facing validation message, or null when ok. */
export function buyerEmailValidationMessage(
  raw: string,
  context: 'checkout' | 'signin' = 'checkout',
): string | null {
  const e = sanitizeBuyerEmailInput(raw)
  if (!e) {
    return context === 'signin' ? 'Enter your email to sign in.' : EMPTY_EMAIL_MSG
  }
  if (!isValidBuyerEmail(e)) return FRIENDLY_EMAIL_MSG
  return null
}

/** Checkout UI: only require a non-empty value; format is checked on the server. */
export function buyerEmailPresenceMessage(raw: string): string | null {
  const e = sanitizeBuyerEmailInput(raw)
  if (!e) return EMPTY_EMAIL_MSG
  return null
}

export function isEmailRelatedCheckoutMessage(msg: string): boolean {
  const t = msg.trim()
  return t === FRIENDLY_EMAIL_MSG || t === EMPTY_EMAIL_MSG || /^enter your email/i.test(t)
}

function parseZodIssuesFromMessage(msg: string): ZodLikeIssue[] | null {
  const trimmed = msg.trim()
  if (!trimmed.startsWith('[')) return null
  try {
    const parsed = JSON.parse(trimmed) as unknown
    return Array.isArray(parsed) ? (parsed as ZodLikeIssue[]) : null
  } catch {
    return null
  }
}

function extractZodIssues(err: unknown): ZodLikeIssue[] | null {
  if (err && typeof err === 'object' && 'issues' in err) {
    const issues = (err as { issues: unknown }).issues
    if (Array.isArray(issues)) return issues as ZodLikeIssue[]
  }
  if (err instanceof Error) return parseZodIssuesFromMessage(err.message)
  return null
}

function issueIsEmailField(issue: ZodLikeIssue): boolean {
  const path = issue.path ?? []
  if (path.length === 1 && path[0] === 'email') return true
  if (path.length === 0 && issue.format === 'email') return true
  return false
}

function zodEmailUserMessage(issue: ZodLikeIssue): string | null {
  if (!issueIsEmailField(issue)) return null
  const msg = issue.message?.trim() ?? ''
  if (/expected string, received undefined/i.test(msg)) return EMPTY_EMAIL_MSG
  if (issue.code === 'too_small' || /before checkout/i.test(msg)) return EMPTY_EMAIL_MSG
  if (issue.code === 'invalid_format' && issue.format === 'email') return FRIENDLY_EMAIL_MSG
  if (/invalid email/i.test(msg) || /email address/i.test(msg)) return FRIENDLY_EMAIL_MSG
  if (msg === FRIENDLY_EMAIL_MSG) return msg
  return null
}

export function friendlyBuyerEmailErrorMessage(err: unknown, fallback: string): string {
  return formatCheckoutError(err, fallback)
}

export function formatCheckoutError(err: unknown, fallback: string): string {
  const issues = extractZodIssues(err)
  if (issues?.length) {
    const otherIssues = issues.filter((i) => !issueIsEmailField(i))
    if (otherIssues.length > 0) {
      const msg = otherIssues[0]?.message?.trim()
      if (msg) return msg
    }
    for (const issue of issues) {
      const mapped = zodEmailUserMessage(issue)
      if (mapped) return mapped
    }
  }

  const msg = err instanceof Error ? err.message.trim() : ''
  if (!msg) return fallback

  if (msg === FRIENDLY_EMAIL_MSG || msg === EMPTY_EMAIL_MSG) return msg
  if (/^invalid email address\.?$/i.test(msg)) return FRIENDLY_EMAIL_MSG

  const cryptoMsg = normalizeCryptoProcessorMessage(msg)
  if (cryptoMsg !== msg) return cryptoMsg

  if (msg.startsWith('[') && msg.includes('"code"')) return fallback

  return msg || fallback
}

const buyerEmailCore = z
  .string()
  .transform(sanitizeBuyerEmailInput)
  .pipe(
    z
      .string()
      .min(1, { message: EMPTY_EMAIL_MSG })
      .max(254)
      .refine((s) => isValidBuyerEmail(s), { message: FRIENDLY_EMAIL_MSG }),
  )

export const buyerEmailZod = buyerEmailCore

export function parseBuyerEmailFromCheckoutPayload(raw: unknown): string {
  const email = sanitizeBuyerEmailInput(typeof raw === 'string' ? raw : String(raw ?? ''))
  if (!email) throw new Error(EMPTY_EMAIL_MSG)
  if (!isValidBuyerEmail(email)) throw new Error(FRIENDLY_EMAIL_MSG)
  return email
}
